import type { Translations } from '@/i18n/types';
import en from '@/i18n/en';
import de from '@/i18n/de';
import fr from '@/i18n/fr';
import es from '@/i18n/es';
import it from '@/i18n/it';
import nl from '@/i18n/nl';

export type Locale = 'en' | 'de' | 'fr' | 'es' | 'it' | 'nl';

const LOCALES: Record<Locale, Translations> = { en, de, fr, es, it, nl };

export const SUPPORTED_LOCALES: { code: Locale; flag: string; label: string; native: string }[] = [
  { code: 'en', flag: '🇬🇧', label: 'English', native: 'English' },
  { code: 'de', flag: '🇩🇪', label: 'German', native: 'Deutsch' },
  { code: 'fr', flag: '🇫🇷', label: 'French', native: 'Français' },
  { code: 'es', flag: '🇪🇸', label: 'Spanish', native: 'Español' },
  { code: 'it', flag: '🇮🇹', label: 'Italian', native: 'Italiano' },
  { code: 'nl', flag: '🇳🇱', label: 'Dutch', native: 'Nederlands' },
];

const STORAGE_KEY = 'app-locale';

/** Detect locale: localStorage → navigator.language → 'en' */
export function detectLocale(): Locale {
  // 1. Check localStorage
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored in LOCALES) return stored as Locale;
  } catch { /* SSR or blocked storage */ }

  // 2. Check navigator.language
  if (typeof navigator !== 'undefined') {
    const lang = navigator.language.split('-')[0];
    if (lang in LOCALES) return lang as Locale;
  }

  // 3. Fallback
  return 'en';
}

/** Persist locale to localStorage */
export function persistLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch { /* blocked storage */ }
}

/** Create a translation function for the given locale */
export function createT(locale: Locale) {
  const messages = LOCALES[locale];
  const fallback = LOCALES.en;

  return function t(key: keyof Translations, params?: Record<string, string | number>): string {
    let value = messages[key] ?? fallback[key] ?? key;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        value = value.replaceAll(`{${k}}`, String(v));
      }
    }
    return value;
  };
}
